import { useState } from 'react';

const BYTES_PER_ROW = 16;
/**
 * Rows drawn before the "show the rest" button. A 256 KB capture is 16 384
 * rows; rendering them all would lock the tab up for seconds on a phone, and
 * nobody reads a binary payload past the first screen anyway.
 */
const INITIAL_ROWS = 32;

export interface HexViewerProps {
  bytes: Uint8Array;
}

/** Classic three-column hex dump: offset, bytes, printable ASCII. */
export function HexViewer({ bytes }: HexViewerProps) {
  const totalRows = Math.ceil(bytes.length / BYTES_PER_ROW);
  const [shownRows, setShownRows] = useState(Math.min(totalRows, INITIAL_ROWS));

  const rows = [];
  for (let row = 0; row < shownRows; row += 1) {
    const start = row * BYTES_PER_ROW;
    rows.push(bytes.subarray(start, start + BYTES_PER_ROW));
  }

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-md bg-slate-950/60 p-3 font-mono text-xs leading-relaxed">
        {rows.map((row, index) => (
          <div key={index} className="whitespace-pre">
            <span className="text-slate-600">
              {(index * BYTES_PER_ROW).toString(16).padStart(8, '0')}
            </span>
            <span className="text-slate-300">
              {'  '}
              {hex(row)}
            </span>
            <span className="text-slate-500">
              {'  '}|{ascii(row)}|
            </span>
          </div>
        ))}
      </div>

      {shownRows < totalRows && (
        <button
          type="button"
          onClick={() => setShownRows(totalRows)}
          className="rounded-md border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs font-medium text-slate-200 hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
        >
          Mostrar los {(totalRows - shownRows).toLocaleString('es-ES')} bytes restantes
        </button>
      )}
    </div>
  );
}

function hex(row: Uint8Array): string {
  const cells = [...row].map((byte) => byte.toString(16).padStart(2, '0'));
  // Pad the last row so the ASCII column stays aligned with the ones above it.
  while (cells.length < BYTES_PER_ROW) cells.push('  ');
  // A wider gap at the halfway mark, as every hex dump has done for decades.
  return `${cells.slice(0, 8).join(' ')}  ${cells.slice(8).join(' ')}`;
}

function ascii(row: Uint8Array): string {
  const text = [...row]
    .map((byte) => (byte >= 0x20 && byte < 0x7f ? String.fromCharCode(byte) : '.'))
    .join('');
  return text.padEnd(BYTES_PER_ROW, ' ');
}
